import React from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { Close, Store } from "@mui/icons-material";
import { dateFormatter } from "../utils/masking";
import type { AddressItem, FavoriteStoreItem, GiftCardItem } from "./ZaxbysAdminCustomerCard";

type Props = {
  giftCardsOpen: boolean;
  onCloseGiftCards: () => void;
  giftCardsList: GiftCardItem[];
  addressOpen: boolean;
  onCloseAddresses: () => void;
  addressesList: AddressItem[];
  favoriteStoresOpen: boolean;
  onCloseFavoriteStores: () => void;
  favoriteStoresList: FavoriteStoreItem[];
};

export function ZaxbysAdminModals({
  giftCardsOpen,
  onCloseGiftCards,
  giftCardsList,
  addressOpen,
  onCloseAddresses,
  addressesList,
  favoriteStoresOpen,
  onCloseFavoriteStores,
  favoriteStoresList,
}: Readonly<Props>) {
  return (
    <>
      <Dialog open={giftCardsOpen} onClose={onCloseGiftCards} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Gift Cards ({giftCardsList.length})
          <IconButton onClick={onCloseGiftCards} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ p: 0 }}>
            {giftCardsList.map((card, index) => (
              <React.Fragment key={card.accountId}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          •••• {card.cardSuffix}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                          ${card.balance.toFixed(2)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Card Number: {card.cardNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Account ID: {card.accountId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Created: {dateFormatter(card.createdAt) ?? "N/A"}
                        </Typography>
                        {card.default && (
                          <Chip label="Default" size="small" color="primary" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < giftCardsList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      <Dialog open={addressOpen} onClose={onCloseAddresses} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Addresses ({addressesList.length})
          <IconButton onClick={onCloseAddresses} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ p: 0 }}>
            {addressesList.map((address, addrIdx) => (
              <React.Fragment
                key={`${address.addressLine1}-${address.city}-${address.postalCode}-${address.latitude}-${address.longitude}`}
              >
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {address.addressLine1}
                        {address.addressLine2 && `, ${address.addressLine2}`}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {address.city}, {address.state} {address.postalCode}
                        </Typography>
                        {address.country && (
                          <Typography variant="body2" color="text.secondary">
                            {address.country}
                          </Typography>
                        )}
                        {address.favorite && (
                          <Chip label="Favorite" size="small" color="secondary" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {addrIdx < addressesList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      <Dialog open={favoriteStoresOpen} onClose={onCloseFavoriteStores} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Favorite Stores ({favoriteStoresList.length})
          <IconButton onClick={onCloseFavoriteStores} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ p: 0 }}>
            {favoriteStoresList.map((store, index) => (
              <React.Fragment key={store.storeId}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Store sx={{ fontSize: 20, color: "#666" }} />
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {store.storeName}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5, ml: 3.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Store ID: {store.storeId}
                        </Typography>
                        {store.isDefault && (
                          <Chip label="Default" size="small" color="primary" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < favoriteStoresList.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
}
